// Implements: F-IDN-027
/**
 * The reads and writes of Personas. The people and the teams (to badge each person's teams) are
 * cached per organization; a role change and a removal are sent once each and re-read the lists.
 *
 * Removing a person moves their mockups of this organization first (`POST /mockups/reassign` with
 * its `organization_id`; their personal mockups stay with them, Default P3-5) and only then removes
 * the seat: when the reassignment is refused, the removal is never sent and the person stays. The
 * lists are re-read however it ends: a removal refused after the move still changed who owns what.
 *
 * Owner 2026-10-19: the dialog first reads who can receive the person's mockups (keyed by the
 * person, so another person's answer never fills it); a person who owns nothing here is removed
 * with no recipient and no reassignment at all.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import type { OrgRole, PeopleList, PersonView, ReassignCandidates, TeamList } from "../../../types/organization";
import {
  fetchPeople,
  fetchReassignCandidates,
  fetchTeams,
  reassignMockups,
  removePerson,
  setPersonRole,
} from "../api/organizationApi";
import { peopleKey, reassignCandidatesKey, refreshOrganization, teamsKey } from "./keys";

export function usePeopleList(organizationId: string): UseQueryResult<PeopleList, ApiError> {
  return useQuery<PeopleList, ApiError>({
    queryKey: peopleKey(organizationId),
    queryFn: ({ signal }) => fetchPeople(organizationId, signal),
  });
}

export function useTeamList(organizationId: string): UseQueryResult<TeamList, ApiError> {
  return useQuery<TeamList, ApiError>({
    queryKey: teamsKey(organizationId),
    queryFn: ({ signal }) => fetchTeams(organizationId, signal),
  });
}

/** Who can receive `userId`'s mockups of this organization; nothing is read while `userId` is `null`. */
export function useReassignCandidates(
  organizationId: string,
  userId: string | null,
): UseQueryResult<ReassignCandidates, ApiError> {
  return useQuery<ReassignCandidates, ApiError>({
    queryKey: reassignCandidatesKey(organizationId, userId ?? ""),
    queryFn: ({ signal }) => fetchReassignCandidates(organizationId, userId ?? "", signal),
    enabled: userId !== null,
  });
}

export interface RoleChange {
  readonly userId: string;
  readonly role: OrgRole;
}

export function useSetPersonRole(organizationId: string): UseMutationResult<PersonView, ApiError, RoleChange> {
  const queryClient = useQueryClient();
  return useMutation<PersonView, ApiError, RoleChange>({
    mutationFn: ({ userId, role }) => setPersonRole(organizationId, userId, role),
    onSettled: () => refreshOrganization(queryClient, organizationId),
  });
}

export interface Removal {
  readonly userId: string;
  /** `null`: the person owns nothing here, so nothing is reassigned before the removal. */
  readonly recipientId: string | null;
}

export function useRemovePerson(organizationId: string): UseMutationResult<null, ApiError, Removal> {
  const queryClient = useQueryClient();
  return useMutation<null, ApiError, Removal>({
    mutationFn: async ({ userId, recipientId }) => {
      if (recipientId !== null) await reassignMockups(organizationId, userId, recipientId);
      await removePerson(organizationId, userId);
      return null;
    },
    onSettled: () => refreshOrganization(queryClient, organizationId),
  });
}
